import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export interface AbuseConfig {
  /**
   * Cloudflare Turnstile site key. Non-null only when (a) abuse protection is
   * enabled in PlatformSetting AND (b) a site key + secret key are configured.
   * Free-tool pages render the Turnstile challenge widget iff this is set.
   */
  turnstileSiteKey: string | null;
}

/**
 * Public read-only hook for the abuse-protection widget config. The endpoint
 * only ever returns the non-secret site key (the secret stays server-side).
 *
 * Defaults to `{ turnstileSiteKey: null }` during the loading window so a
 * brief flicker doesn't gate the form on a widget that isn't actually
 * configured. The backend guard short-circuits anyway when no secret is set,
 * so a missed token during the loading window is harmless.
 */
export function useAbuseConfig(): AbuseConfig {
  const { data } = useQuery({
    queryKey: ['abuse-config'],
    queryFn: () => api<AbuseConfig>('/platform/abuse-config', { auth: false }),
    // Tools pages are mostly transient visits — caching for 5 minutes is plenty.
    staleTime: 5 * 60_000,
  });
  return data ?? { turnstileSiteKey: null };
}

/**
 * Companion to `useAbuseConfig` for the 7 free-tool pages. Bundles together
 * the site key, a token state slot, and the `headers` object that should be
 * splatted into the page's `api(..., { headers })` call.
 *
 * Two semantically distinct "ready" states matter to the form:
 *   - `siteKey` is null (Turnstile off) → `ready = true`; the form can submit
 *     immediately and `headers` is an empty object.
 *   - `siteKey` is set but `token` not yet emitted → `ready = false`; the
 *     submit button should be disabled until the widget callback fires.
 *
 * `armed` defers the Cloudflare challenge off the initial page load. Turnstile
 * costs ~1.3 MB decoded across 6 extra third-party requests (an 800 KB
 * challenge document + a 256 KB `turns` payload + `api.js`), which on the
 * public tool pages more than DOUBLED total page weight — 0.89 MB → 2.19 MB —
 * and it is third-party JS on the main thread, so it taxes INP too. Most
 * visitors read a tool page without ever submitting, so they should not pay
 * for the challenge up front.
 *
 * Spread `armProps` on the element wrapping the form: the widget mounts on the
 * visitor's first real interaction with it, which is many seconds ahead of the
 * submit click, so the token is there by the time it is needed.
 *
 * Deliberately NOT keyed on focus: the tool forms `autoFocus` their first
 * input, and that programmatic focus fires a real focus event on mount, which
 * would arm the gate instantly and defeat the deferral. `pointerdown` /
 * `keydown` / `touchstart` are only produced by an actual visitor — clicking
 * into the field, typing into an autofocused one, or tabbing in and typing.
 *
 * This does NOT change the widget's `appearance: 'always'` — the success badge
 * still renders visibly under every gated form, just from first interaction
 * rather than from page load. See `TurnstileWidget` for why that matters.
 */
export function useTurnstileGate() {
  const { turnstileSiteKey } = useAbuseConfig();
  const [token, setToken] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);
  const arm = useCallback(() => setArmed(true), []);
  return {
    siteKey: turnstileSiteKey,
    setToken,
    /** True once the visitor has interacted with the gated form. */
    armed,
    /** Spread onto the element wrapping the gated form. */
    armProps: { onPointerDown: arm, onKeyDown: arm, onTouchStart: arm },
    /**
     * Drop-in for `api(..., { headers })`. The X-Turnstile-Token header is
     * only set when a token is available — undefined values are dropped by
     * the api helper.
     */
    headers: { 'X-Turnstile-Token': token ?? undefined } as Record<string, string | undefined>,
    /** True when the form may submit (Turnstile off, or token present). */
    ready: !turnstileSiteKey || !!token,
  };
}
